import * as pc from "playcanvas";

import { EPSILON } from "../constants";
import type { Number9 } from "../types";
import type { ReadonlyMatrix4 } from "./Matrix4";
import type { ReadonlyQuaternion } from "./Quaternion";
import type { ReadonlyVector3, Vector3 } from "./Vector3";

export interface ReadonlyMatrix3 {
  get data(): Float32Array;

  clone(): Matrix3;
  equals(rhs: ReadonlyMatrix3): boolean;
  getX(x?: Vector3): Vector3;
  getY(y?: Vector3): Vector3;
  getZ(z?: Vector3): Vector3;
  isIdentity(): boolean;
  toString(): string;
  transformVector(vec: ReadonlyVector3, res?: Vector3): Vector3;
  toArray(): Number9;
  copyToArray(array: ArrayLike<number>, offset?: number): this;

  equalsApprox(rhs: ReadonlyMatrix3, epsilon?: number): boolean;
  isApproximately(rhs: ReadonlyMatrix3, epsilon?: number): boolean;
  toJSON(): number[];

  // Convert for use in external JS code.
  // This is unsafe and should only be used when necessary.
  unsafeCast(): Matrix3;
}

declare module "playcanvas" {
  interface Mat3 extends ReadonlyMatrix3 {
    data: Float32Array;

    copy(rhs: ReadonlyMatrix3): this;
    invertMat4(src: ReadonlyMatrix4): this;
    set(src: Readonly<ArrayLike<number>>): this;
    setFromMat4(m: ReadonlyMatrix4): this;
    setFromQuat(r: ReadonlyQuaternion): this;
    setIdentity(): this;
    transpose(src?: ReadonlyMatrix3): this;
    toArray(): Number9;
    copyToArray(array: ArrayLike<number>, offset?: number): this;

    fromArray(array: Number9): this;
    recycle(): void;
  }
}

pc.Mat3.prototype.fromArray = function (array: Number9): Matrix3 {
  const dst = this.data as Float32Array;
  dst[0] = array[0];
  dst[1] = array[1];
  dst[2] = array[2];
  dst[3] = array[3];
  dst[4] = array[4];
  dst[5] = array[5];
  dst[6] = array[6];
  dst[7] = array[7];
  dst[8] = array[8];
  return this;
};

/**
 * @summary Converts the matrix to a JSON array.
 */
pc.Mat3.prototype.toJSON = function (): number[] {
  return [
    this.data[0],
    this.data[1],
    this.data[2],
    this.data[3],
    this.data[4],
    this.data[5],
    this.data[6],
    this.data[7],
    this.data[8],
  ];
};

/**
 * @summary Recycles the matrix by pushing it back to the pool.
 */
pc.Mat3.prototype.recycle = function (): void {
  pool.push(this);
};

/**
 * @summary Checks if the matrix is approximately equal to another matrix.
 */
pc.Mat3.prototype.equalsApprox = function (rhs: ReadonlyMatrix3, epsilon = EPSILON): boolean {
  const l = this.data;
  const r = rhs.data;
  return (
    Math.abs(l[0] - r[0]) <= epsilon &&
    Math.abs(l[1] - r[1]) <= epsilon &&
    Math.abs(l[2] - r[2]) <= epsilon &&
    Math.abs(l[3] - r[3]) <= epsilon &&
    Math.abs(l[4] - r[4]) <= epsilon &&
    Math.abs(l[5] - r[5]) <= epsilon &&
    Math.abs(l[6] - r[6]) <= epsilon &&
    Math.abs(l[7] - r[7]) <= epsilon &&
    Math.abs(l[8] - r[8]) <= epsilon
  );
};

/**
 * @summary Checks if the matrix is approximately equal to another matrix.
 */
pc.Mat3.prototype.isApproximately = function (rhs: ReadonlyMatrix3, epsilon = EPSILON): boolean {
  return this.equalsApprox(rhs, epsilon);
};

pc.Mat3.prototype.unsafeCast = function () {
  return this;
};

const pool: Matrix3[] = [];

// @ts-expect-error - Override static constants type (intentionally incompatible with base class)
export class Matrix3 extends pc.Mat3 {
  static override IDENTITY = Matrix3.make().setIdentity() as ReadonlyMatrix3;

  /**
   * @summary Creates a new matrix with identity values.
   */
  static make(data?: Readonly<Float32Array>): Matrix3 {
    const mat = pool.pop()?.setIdentity() ?? new Matrix3();
    if (data) {
      if (data.length !== 9) {
        throw new Error("Matrix3 data must be a Float32Array of length 9");
      }
      mat.set(data);
    }
    return mat;
  }

  /**
   * @summary Creates a new readonly matrix with identity values.
   */
  static makeReadonly(): ReadonlyMatrix3 {
    const matrix = new Matrix3();
    Object.freeze(matrix);
    return matrix;
  }

  /**
   * @summary Creates a new uninitialized matrix.
   */
  static makeUninitialized(): Matrix3 {
    return pool.pop() || new Matrix3();
  }
}
